import React, {useEffect, useRef, useState} from 'react';
import {useLocation, useNavigate} from 'react-router';
import {
    Button,
    Divider,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import {useSession} from '../../firebase/UserProvider';
import {createIcr} from '../../firebase/firestore/icr.js';
import {updateReportDoc} from '../../firebase/firestore/reports';
import {scheduleExpertProfilePipelineAfterFintelApproval} from '../experts/expertProfileService';
import * as ICRModule from './_test_data/ICRequests';
import {getValidationStepsForProductType} from '../../apps/reviewer/pages/product/constants/validationStepSelectors';
import {
    buildPirSavePayload,
    resolvePirEeis,
    resolvePirQuestion,
    resolvePirTimeframe,
    resolvePirTitle,
} from './pirPayload';
import EeiListInput from './components/EeiListInput';
import RequirementTagsInput from './components/RequirementTagsInput';
import EntityTargetsInput from './components/EntityTargetsInput';

import {EEI_PRIORITIES, normalizeEeiPriority} from '../../firebase/firestore/eei';
const CLASSIFICATION_OPTIONS = [
    'UNCLASSIFIED',
    'UNCLASSIFIED//FOUO',
    'CONFIDENTIAL',
    'SECRET',
    'SECRET//REL TO USA, FVEY',
    'TOP SECRET',
    'TOP SECRET//SCI',
];

function toDateInput(value) {
    if (!value) return '';
    return dayjs(value).isValid() ? dayjs(value).format('YYYY-MM-DD') : '';
}

function eeisToNewLines(prefill = {}) {
    if (Array.isArray(prefill?.newEeis) && prefill.newEeis.length > 0) {
        return prefill.newEeis.map((t) => (typeof t === 'string' ? t : t?.text || '')).filter((t) => t !== undefined);
    }
    if (Array.isArray(prefill?.eeisLines) && prefill.eeisLines.length > 0) {
        return prefill.eeisLines.map((t) => String(t ?? ''));
    }
    const fromResolved = resolvePirEeis(prefill).map((e) => e.text).filter(Boolean);
    if (fromResolved.length > 0) return fromResolved;
    const legacy = [prefill?.eeiWhat, prefill?.eeiWhere, prefill?.eeiWhen, prefill?.eeiDetail].filter(Boolean);
    if (legacy.length > 0) return legacy;
    if (prefill?.eeisText) return String(prefill.eeisText).split(/\r?\n/);
    return [''];
}

function buildFormFromPrefill(prefill = {}) {
    const tf = resolvePirTimeframe(prefill);
    const selected = resolvePirEeis(prefill).filter((e) => e.id && !String(e.id).includes('-EEI-'));
    return {
        title: resolvePirTitle(prefill) || prefill?.title || prefill?.subject || '',
        question: resolvePirQuestion(prefill) || prefill?.question || '',
        supportedDecision: prefill?.supportedDecision || '',
        priority: normalizeEeiPriority(prefill?.priority || 'Medium'),
        owner: prefill?.owner || '',
        timeframeStart: toDateInput(tf.start || prefill?.timeframeStart),
        timeframeDue: toDateInput(tf.due || prefill?.timeframeDue || prefill?.expirationDate),
        areaOfInterest: prefill?.areaOfInterest || '',
        classification: prefill?.classification || 'UNCLASSIFIED',
        tags: Array.isArray(prefill?.tags) ? prefill.tags : [],
        targets: Array.isArray(prefill?.targets) ? prefill.targets : [],
        eeiIds: Array.isArray(prefill?.eeiIds) ? prefill.eeiIds : selected.map((e) => e.id),
        selectedEeis: selected,
        newEeis: selected.length > 0 ? [] : eeisToNewLines(prefill).filter(Boolean),
        gapIds: Array.isArray(prefill?.gapIds) ? prefill.gapIds : [],
        pendingGaps: Array.isArray(prefill?.pendingGaps) ? prefill.pendingGaps : [],
        manageEeis: true,
        currentAssessment: prefill?.currentAssessment || '',
    };
}

export default function NewIcrPage({
    embedded = false,
    prefill: prefillProp = null,
    llmBaseline: llmBaselineProp = null,
    sourceContext = null,
    onCreated,
    onDraftSaved,
    onError,
    onCancel,
}) {
    const navigate = useNavigate();
    const location = useLocation();
    const {user, role, roles} = useSession();
    const canManageGaps = role === 'collection_manager' || role === 'admin'
        || (roles || []).some((r) => r === 'collection_manager' || r === 'admin');
    const routeState = location.state || {};
    const prefill = prefillProp ?? routeState.prefill ?? null;
    const effectiveSourceContext = sourceContext || routeState || {};
    const sourceReportId = effectiveSourceContext.sourceReportId || null;
    const sourceReportMeta = effectiveSourceContext.sourceReportMeta || {};
    const sourceProductTypeValue = effectiveSourceContext.sourceProductTypeValue || null;
    const sourceValidation = effectiveSourceContext.sourceValidation || {};
    const validationStepIndex = Number.isInteger(effectiveSourceContext.validationStepIndex)
        ? effectiveSourceContext.validationStepIndex
        : null;
    const returnTo = effectiveSourceContext.returnTo || null;
    const sourceSnapshotPath = effectiveSourceContext.sourceSnapshotPath || null;
    const [submitting, setSubmitting] = useState(false);
    const [form, setForm] = useState(() => buildFormFromPrefill(prefill));
    const llmBaselineRef = useRef(llmBaselineProp || prefill || null);

    useEffect(() => {
        if (prefill) setForm(buildFormFromPrefill(prefill));
    }, [prefill]);

    useEffect(() => {
        if (llmBaselineProp) llmBaselineRef.current = llmBaselineProp;
    }, [llmBaselineProp]);

    const populateRandom = () => {
        try {
            const list = ICRModule.ICR_TEST_DATA || (ICRModule.default && ICRModule.default.ICR_TEST_DATA) || [];
            if (!Array.isArray(list) || list.length === 0) return;
            const item = list[Math.floor(Math.random() * list.length)];
            const now = dayjs();
            const due = now.add(Math.floor(Math.random() * 174) + 7, 'day').format('YYYY-MM-DD');
            const start = now.format('YYYY-MM-DD');
            setForm(buildFormFromPrefill({
                ...item,
                owner: item.owner || item.requestor || '',
                areaOfInterest: item.areaOfInterest || item.targetInformation?.geographicArea || '',
                timeframeStart: start,
                timeframeDue: due,
                timeframe: {start, due},
                eeiIds: [],
                selectedEeis: [],
                gapIds: [],
                pendingGaps: [],
            }));
        } catch (err) {
            console.error('Failed to populate random ICR:', err);
        }
    };

    const buildIcrPayload = () => ({
        ...buildPirSavePayload(form),
        createdBy: user?.uid || null,
        createdAt: dayjs().toISOString(),
        sourceReportId: sourceReportId || null,
        sourceProductType: sourceProductTypeValue || null,
    });

    const buildPitchReportUpdate = (created) => {
        const nextValidation = {...(sourceValidation || {})};
        if (validationStepIndex !== null && validationStepIndex >= 0) {
            nextValidation[validationStepIndex] = {
                passed: true,
                feedback: `ICR created: ${created.icrNumber}`,
            };
        }
        const steps = getValidationStepsForProductType(sourceProductTypeValue || 'pitch');
        const allApproved = steps.every((_, idx) => nextValidation?.[idx]?.passed === true);
        const reportUpdatePayload = {
            meta: {
                ...(sourceReportMeta || {}),
                icrNumber: created.icrNumber,
            },
            validation: nextValidation,
        };
        if (allApproved) reportUpdatePayload.status = 'approved';
        return reportUpdatePayload;
    };

    const onSubmit = async () => {
        if (submitting) return;
        setSubmitting(true);
        try {
            const created = await createIcr(buildIcrPayload());
            let reportUpdatePayload = null;
            if (sourceReportId) {
                reportUpdatePayload = buildPitchReportUpdate(created);
                await updateReportDoc(sourceReportId, reportUpdatePayload);
                if (reportUpdatePayload.status === 'approved') {
                    scheduleExpertProfilePipelineAfterFintelApproval(sourceReportId, sourceSnapshotPath);
                }
            }
            if (onCreated) {
                await onCreated({createdIcr: created, reportUpdatePayload});
                return;
            }
            navigate(returnTo || `/icr/${encodeURIComponent(created.icrNumber)}`);
        } catch (error) {
            console.error('Failed to create ICR', error);
            onError?.(error);
        } finally {
            setSubmitting(false);
        }
    };

    const onSaveDraft = async () => {
        if (submitting || !sourceReportId || validationStepIndex === null || validationStepIndex < 0) return;
        setSubmitting(true);
        try {
            const stepEntry = {
                passed: false,
                feedback: 'ICR draft saved',
                draftForm: buildFormFromPrefill(form),
            };
            if (llmBaselineRef.current) stepEntry.llmBaseline = llmBaselineRef.current;
            const nextValidation = {
                ...(sourceValidation || {}),
                [validationStepIndex]: stepEntry,
            };
            await updateReportDoc(sourceReportId, {validation: nextValidation});
            await onDraftSaved?.({validation: nextValidation});
        } catch (error) {
            console.error('Failed to save ICR draft', error);
            onError?.(error);
        } finally {
            setSubmitting(false);
        }
    };

    const onReset = () => {
        const baseline = llmBaselineProp || llmBaselineRef.current || prefill;
        if (!baseline) return;
        setForm(buildFormFromPrefill(baseline));
    };

    const handleCancel = () => {
        if (onCancel) {
            onCancel();
            return;
        }
        navigate(-1);
    };

    const setField = (key) => (e) => setForm((f) => ({...f, [key]: e.target.value}));

    const formContent = (
        <Stack p={embedded ? 2 : 4} pt={embedded ? 2 : 3} spacing={3} bgcolor="white" borderRadius={2}
               boxShadow={embedded ? 0 : 1} width="100%" maxWidth={720}>
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                <Typography variant="h5">
                    {sourceReportId ? 'Create ICR from Pitch' : 'New ICR'}
                </Typography>
                <Button size="small" variant="outlined" onClick={populateRandom}>Populate Random</Button>
            </Stack>

            {sourceReportId ? (
                <Typography variant="body2" color="text.secondary">
                    Prefilled from pitch metadata. Review and edit before creating this linked ICR.
                    {sourceProductTypeValue ? ` Source type: ${sourceProductTypeValue}.` : ''}
                </Typography>
            ) : null}

            <Typography variant="h6">General</Typography>
            <TextField fullWidth label="Title" value={form.title} onChange={setField('title')}/>

            <TextField fullWidth multiline minRows={3} label="Question"
                       value={form.question} onChange={setField('question')}/>

            <TextField fullWidth label="Supported Decision"
                       value={form.supportedDecision} onChange={setField('supportedDecision')}/>

            <Stack direction={{xs: 'column', sm: 'row'}} spacing={1}>
                <FormControl fullWidth>
                    <InputLabel id="priority-label">Priority</InputLabel>
                    <Select labelId="priority-label" label="Priority"
                            value={normalizeEeiPriority(form.priority)}
                            onChange={(e) => setForm((f) => ({...f, priority: e.target.value}))}>
                        {EEI_PRIORITIES.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                    </Select>
                </FormControl>
                <TextField fullWidth label="Owner" value={form.owner} onChange={setField('owner')}/>
                <FormControl fullWidth>
                    <InputLabel id="classification-label">Classification</InputLabel>
                    <Select labelId="classification-label" label="Classification" value={form.classification}
                            onChange={(e) => setForm((f) => ({...f, classification: e.target.value}))}>
                        {CLASSIFICATION_OPTIONS.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                    </Select>
                </FormControl>
            </Stack>

            <Stack direction={{xs: 'column', sm: 'row'}} spacing={1}>
                <TextField fullWidth type="date" label="Timeframe Start" InputLabelProps={{shrink: true}}
                           value={form.timeframeStart} onChange={setField('timeframeStart')}/>
                <TextField fullWidth type="date" label="Timeframe Due" InputLabelProps={{shrink: true}}
                           value={form.timeframeDue} onChange={setField('timeframeDue')}/>
            </Stack>

            <TextField fullWidth label="Area of Interest"
                       value={form.areaOfInterest} onChange={setField('areaOfInterest')}/>

            <TextField fullWidth multiline minRows={2} label="Current Assessment"
                       value={form.currentAssessment} onChange={setField('currentAssessment')}/>

            <RequirementTagsInput
                value={form.tags}
                onChange={(tags) => setForm((f) => ({...f, tags}))}
            />

            <EntityTargetsInput
                value={form.targets}
                onChange={(targets) => setForm((f) => ({...f, targets}))}
            />

            <Divider sx={{my: 1}}/>

            <EeiListInput
                value={{
                    eeiIds: form.eeiIds || [],
                    newEeis: form.newEeis || [],
                    gapIds: form.gapIds || [],
                    pendingGaps: form.pendingGaps || [],
                }}
                initialSelected={form.selectedEeis || []}
                allowFindGap={canManageGaps}
                onChange={({eeiIds, newEeis, selectedEeis, gapIds, pendingGaps}) => setForm((f) => ({
                    ...f,
                    eeiIds,
                    newEeis,
                    selectedEeis: selectedEeis || f.selectedEeis,
                    gapIds: gapIds || [],
                    pendingGaps: pendingGaps || [],
                }))}
            />

            <Divider sx={{my: 1}}/>

            {embedded ? (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Button variant="contained" onClick={onSubmit} disabled={submitting}>
                        {submitting ? 'Creating...' : 'Create'}
                    </Button>
                    <Button variant="outlined" onClick={onSaveDraft} disabled={submitting || !sourceReportId}>
                        Save as Draft
                    </Button>
                    <Button variant="outlined" onClick={onReset} disabled={submitting || !(llmBaselineProp || prefill)}>
                        Reset
                    </Button>
                    <Button variant="text" onClick={handleCancel} disabled={submitting}>Cancel</Button>
                </Stack>
            ) : (
                <Stack direction="row" spacing={2}>
                    <Button variant="contained" onClick={onSubmit} disabled={submitting}>
                        {submitting ? 'Creating...' : 'Create ICR'}
                    </Button>
                    <Button variant="text" onClick={handleCancel} disabled={submitting}>Cancel</Button>
                </Stack>
            )}
        </Stack>
    );

    if (embedded) return formContent;
    return (
        <Stack p={2} spacing={2} alignItems="center">
            {formContent}
        </Stack>
    );
}
